import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Paper,
  Alert,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  IconButton,
  useTheme,
  Chip
} from '@mui/material';
import axios from '../../config/axiosConfig.js';
import { useNotifications } from '../../contexts/NotificationContext';

// Icons
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import ClearIcon from '@mui/icons-material/Clear';

// Components
import NotificationItem from './NotificationItem';
import NotificationDetailModal from './NotificationDetailModal';

const NotificationsList = ({ setSuccessMessage, setErrorMessage }) => {
  const theme = useTheme();
  const { notifications, unreadCount, markAsRead, markAllAsRead, setNotifications } = useNotifications();

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [readFilter, setReadFilter] = useState('all');
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Set items per page for UI pagination
  const ITEMS_PER_PAGE = 15;

  // Current page state for UI pagination
  const [currentPage, setCurrentPage] = useState(1);

  // REMOVED useEffect that was calling fetchAllNotifications()

  // Process notifications with filters
  const filteredNotifications = notifications.filter(notification => {
    // Search term filter
    if (searchTerm &&
      !notification.title?.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !notification.content?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    // Type filter
    if (typeFilter !== 'all' && notification.type !== typeFilter) {
      return false;
    }

    // Read status filter
    if (readFilter === 'read' && !notification.isRead) {
      return false;
    }
    if (readFilter === 'unread' && notification.isRead) {
      return false;
    }

    return true;
  });

  // Calculate total pages based on filtered notifications
  const totalPages = Math.ceil(filteredNotifications.length / ITEMS_PER_PAGE);

  // Get notifications for current page - UI PAGINATION ONLY
  const paginatedNotifications = filteredNotifications.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Get count of each notification type for the filter dropdown
  const getTypeCount = (type) => {
    if (type === 'all') return notifications.length;
    return notifications.filter(notification => notification.type === type).length;
  };

  // Get read count (for clear dialog)
  const readCount = notifications.filter(notification => notification.isRead).length;

  // Handle page change - UI PAGINATION ONLY
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Handle opening clear dialog
  const handleOpenClearDialog = () => {
    setClearDialogOpen(true);
  };

  // Handle closing clear dialog
  const handleCloseClearDialog = () => {
    setClearDialogOpen(false);
  };

  // Handle clear read notifications - with proper API call
  const handleClearReadNotifications = async () => {
    try {
      // Call the API endpoint to clear read notifications
      await axios.post('/api/notifications/clear-read');

      // Update local state - remove read notifications
      const unreadNotifications = notifications.filter(notification => !notification.isRead);
      setNotifications(unreadNotifications);

      // Show success message
      setSuccessMessage && setSuccessMessage('Read notifications cleared successfully');

      // Close the dialog
      setClearDialogOpen(false);
    } catch (error) {
      console.error('Error clearing read notifications:', error);
      setErrorMessage && setErrorMessage('Failed to clear read notifications. Please try again.');
      // Keep the dialog open on error
    }
  };

  // Handle viewing notification details
  const handleViewDetails = (notification) => {
    setSelectedNotification(notification);
    setDetailModalOpen(true);
  };

  // Handle closing detail modal
  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedNotification(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">
          Your Notifications
          {unreadCount > 0 && (
            <Chip
              size="small"
              label={unreadCount}
              color="primary"
              sx={{ ml: 1, height: 20, fontSize: '0.75rem' }}
            />
          )}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {unreadCount > 0 && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<DoneAllIcon />}
              onClick={markAllAsRead}
            >
              Mark All as Read
            </Button>
          )}

          {readCount > 0 && (
            <Tooltip title="Clear read notifications">
              <IconButton
                color="default"
                onClick={handleOpenClearDialog}
                sx={{
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  p: 1,
                  '&:hover': {
                    bgcolor: theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.05)'
                      : 'rgba(0, 0, 0, 0.05)',
                  }
                }}
              >
                <CleaningServicesIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', mb: 3, gap: 2 }}>
        <TextField
          placeholder="Search notifications..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1); // Reset to first page when search changes
          }}
          sx={{ flexGrow: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear search"
                  onClick={() => {
                    setSearchTerm('');
                    setCurrentPage(1); // Reset to first page when search is cleared
                  }}
                  edge="end"
                  size="small"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="notification-type-filter-label">Type</InputLabel>
            <Select
              labelId="notification-type-filter-label"
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                setCurrentPage(1); // Reset to first page on filter change
              }}
              label="Type"
              startAdornment={
                <FilterListIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
              }
            >
              <MenuItem value="all">All Types ({getTypeCount('all')})</MenuItem>
              <MenuItem value="priceAlert">Price Alerts ({getTypeCount('priceAlert')})</MenuItem>
              <MenuItem value="congressTrade">Congressional ({getTypeCount('congressTrade')})</MenuItem>
              <MenuItem value="newsAlert">News Alerts ({getTypeCount('newsAlert')})</MenuItem>
              <MenuItem value="insiderTrade">Insider Trading ({getTypeCount('insiderTrade')})</MenuItem>
              <MenuItem value="marketEvent">Market Events ({getTypeCount('marketEvent')})</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel id="read-status-filter-label">Status</InputLabel>
            <Select
              labelId="read-status-filter-label"
              value={readFilter}
              onChange={(e) => {
                setReadFilter(e.target.value);
                setCurrentPage(1); // Reset to first page on filter change
              }}
              label="Status"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="unread">Unread</MenuItem>
              <MenuItem value="read">Read</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {notifications.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: theme.palette.mode === 'dark'
              ? 'rgba(19, 47, 76, 0.4)'
              : 'rgba(255, 255, 255, 0.8)',
            borderColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(0, 0, 0, 0.1)',
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No notifications yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            When you receive notifications, they'll appear here.
          </Typography>
        </Paper>
      ) : filteredNotifications.length === 0 ? (
        <Alert severity="info">
          No notifications match your current filters. Try adjusting your search or filters.
        </Alert>
      ) : (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing {paginatedNotifications.length} of {filteredNotifications.length} notifications
          </Typography>

          {paginatedNotifications.map(notification => (
            <NotificationItem
              key={notification._id}
              notification={notification}
              onMarkAsRead={markAsRead}
              onViewDetails={handleViewDetails}
            />
          ))}

          {/* UI Pagination Only */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="medium"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: 1,
                  }
                }}
              />
            </Box>
          )}
        </Box>
      )}

      {/* Clear Notifications Dialog */}
      <Dialog
        open={clearDialogOpen}
        onClose={handleCloseClearDialog}
        aria-labelledby="clear-dialog-title"
        PaperProps={{
          sx: {
            bgcolor: theme.palette.mode === 'dark'
              ? 'rgba(19, 47, 76, 0.8)'
              : 'rgba(255, 255, 255, 0.9)',
            backgroundImage: 'none',
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <DialogTitle id="clear-dialog-title">
          Clear alerts log
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Do you really want to remove all read alert records from the log?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={handleCloseClearDialog}
            variant="outlined"
            sx={{
              color: theme.palette.mode === 'dark' ? 'white' : 'black',
              borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.23)',
              '&:hover': {
                borderColor: theme.palette.mode === 'dark' ? 'white' : 'black',
                bgcolor: 'transparent'
              }
            }}
          >
            No
          </Button>
          <Button
            onClick={handleClearReadNotifications}
            variant="contained"
            color="error"
            sx={{ ml: 1 }}
          >
            Clear
          </Button>
        </DialogActions>
      </Dialog>

      <NotificationDetailModal
        notification={selectedNotification}
        open={detailModalOpen}
        onClose={handleCloseDetailModal}
      />
    </Box>
  );
};

export default NotificationsList;